import { TeachingLoadReportType } from '@bp/planung-graphql-types';
import {
  AddIcon,
  Button,
  ButtonGroup,
  DeleteIcon,
  EmptyState,
  ImportIcon,
  NoTasksIcon,
  Table,
  TableColumns,
} from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../hooks/useConfirm';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { useTeachingLoadReportsQuery } from '../../../types/planung-graphql-client-defs';
import {
  createBerlinExcelReport,
  createTeachingLoadExcelReport,
  createTeachingLoadLelisteReport,
  deleteExcelReport,
  downloadExcelReport,
} from '../../../utils/excelReport';
import { createTeachingLoadReport, deleteReport, downloadReport } from '../../../utils/pdfReport';

type TeachingLoadReportsTableType = {
  uuid: string;
  name: string;
  contextUuid: string;
  contextName: string;
  timestamp: dayjs.Dayjs;
};

type TeachingLoadReportsTableProps = {
  showEmptyState: boolean;
  subType: TeachingLoadReportType;
  timetableBlockUuid: string;
};

export const TeachingLoadReportsTable = observer(
  ({ showEmptyState, subType, timetableBlockUuid }: TeachingLoadReportsTableProps) => {
    const { t } = useTranslation();
    const currentSchoolYear = useSelectedSchoolYear();
    const permissionChecker = usePermissionChecker();
    const schoolYearUuid = currentSchoolYear?.uuid;

    const [loading, setLoading] = useState<string | 'all' | null>(null);

    const { ConfirmationDialog, confirm } = useConfirm({
      defaultMessage: t('delete.message', { context: 'female', count: 1, type: t('pdf.title', { count: 1 }) }),
    });

    // 'all' meint die Berichte des Schuljahres; sonst ist es die uuid eines Zeitraums oder eines Epochenplans –
    // welches von beiden, weiß die Tabelle nicht, deshalb steht die uuid in beiden Listen.
    const forSchoolYear = timetableBlockUuid === 'all';
    const [{ data: teachingLoadReportsData }, refetchReports] = useTeachingLoadReportsQuery({
      variables: forSchoolYear
        ? { type: subType, schoolYearUuid }
        : { type: subType, teachingBlockUuids: [timetableBlockUuid], timetableBlockUuids: [timetableBlockUuid] },
      pause: forSchoolYear ? !schoolYearUuid : !timetableBlockUuid,
      // Berichte entstehen über die PDF- und Excel-Routen; davon erfährt der Cache nichts.
      requestPolicy: 'cache-and-network',
    });

    // Stabile Referenzen: die Tabelle setzt bei neuen data/columns ihren Zustand zurück.
    const columns: TableColumns<TeachingLoadReportsTableType>[] = useMemo(
      () => [
        {
          header: '',
          id: 'name',
          accessorKey: 'name',
          size: 480,
        },
        {
          header: '',
          id: 'timestamp',
          accessorFn: (row) => {
            return row.timestamp.format('DD.MM.YY HH:mm');
          },
          size: 120,
        },
      ],
      [],
    );

    const data: TeachingLoadReportsTableType[] = useMemo(
      () =>
        teachingLoadReportsData?.teachingLoadReports.map((report) => {
          return {
            uuid: report.uuid,
            name: report.title,
            contextUuid: report.contextUuid,
            contextName: report.contextName,
            timestamp: dayjs(report.jobFinished),
          };
        }) ?? [],
      [teachingLoadReportsData?.teachingLoadReports],
    );

    async function handleDownload(report: TeachingLoadReportsTableType) {
      if (loading) return;
      setLoading(report.uuid);
      if (
        subType === TeachingLoadReportType.PlanExcel ||
        subType === TeachingLoadReportType.CurrentExcel ||
        subType === TeachingLoadReportType.PlanLeliste ||
        subType === TeachingLoadReportType.CurrentLeliste ||
        subType === TeachingLoadReportType.PlanExcelBerlin ||
        subType === TeachingLoadReportType.CurrentExcelBerlin
      ) {
        await downloadExcelReport(report, subType);
      } else {
        void (await downloadReport(report, subType));
      }
      setLoading(null);
    }

    async function handleDelete(report: TeachingLoadReportsTableType) {
      if (loading) return;
      const result = await confirm();
      if (!result) return;
      setLoading(report.uuid);
      if (
        subType === TeachingLoadReportType.PlanExcel ||
        subType === TeachingLoadReportType.CurrentExcel ||
        subType === TeachingLoadReportType.PlanLeliste ||
        subType === TeachingLoadReportType.CurrentLeliste ||
        subType === TeachingLoadReportType.PlanExcelBerlin ||
        subType === TeachingLoadReportType.CurrentExcelBerlin
      ) {
        await deleteExcelReport(report, subType);
      } else {
        await deleteReport(report, subType);
      }
      refetchReports({ requestPolicy: 'network-only' });
      setLoading(null);
    }

    async function handleGenerate() {
      if (loading) return;
      setLoading('all');

      if (subType === TeachingLoadReportType.PlanExcel) {
        // TODO fix in BE. add subType to excel route !
        const _timetableBlockUuid = timetableBlockUuid === 'all' ? 'default' : timetableBlockUuid;
        await createTeachingLoadExcelReport(currentSchoolYear?.uuid ?? '', _timetableBlockUuid);
      } else if (subType === TeachingLoadReportType.CurrentExcel) {
        await createTeachingLoadExcelReport(currentSchoolYear?.uuid ?? '', timetableBlockUuid ?? 'all');
      } else if (subType === TeachingLoadReportType.CurrentLeliste) {
        await createTeachingLoadLelisteReport(currentSchoolYear?.uuid ?? '', timetableBlockUuid ?? 'all');
      } else if (subType === TeachingLoadReportType.PlanLeliste) {
        await createTeachingLoadLelisteReport(currentSchoolYear?.uuid ?? '', 'default');
      } else if (
        subType === TeachingLoadReportType.PlanExcelBerlin ||
        subType === TeachingLoadReportType.CurrentExcelBerlin
      ) {
        await createBerlinExcelReport(currentSchoolYear?.uuid ?? '', timetableBlockUuid ?? 'all');
      } else {
        await createTeachingLoadReport(
          currentSchoolYear?.uuid ?? '',
          !timetableBlockUuid || timetableBlockUuid === '' ? 'default' : timetableBlockUuid,
          'teaching-load',
          subType,
        );
      }
      refetchReports({ requestPolicy: 'network-only' });
      setLoading(null);
    }

    return (
      <>
        {!showEmptyState ? (
          <Table<TeachingLoadReportsTableType>
            data={data}
            columns={columns}
            hideHeader
            showActionBar
            actionBarSettings={{
              globalFilterPlaceholder: t('common.search'),
              extendedActionsRight: (
                <Button isLoading={loading === 'all'} icon={<AddIcon />} onClick={handleGenerate}>
                  {t('common.generate')}
                </Button>
              ),
            }}
            emptyStateSettings={{ size: 'small', hideIcon: true, subtitle: '', title: t('pdf.noPdfFound') }}
            customPadding='var(--spacing-6)'
            lastColWidth='69px'
            lastCol={({ original }) => (
              <ButtonGroup loading={loading === original.uuid}>
                <Button
                  hierarchy='secondary'
                  icon={<ImportIcon className='small' />}
                  onClick={() => handleDownload(original)}
                />
                <Button
                  disabled={!permissionChecker?.canWriteDeputate()}
                  hierarchy='secondary'
                  icon={<DeleteIcon className='small' />}
                  onClick={() => handleDelete(original)}
                />
              </ButtonGroup>
            )}
          />
        ) : (
          <EmptyState title={t('common.commingSoon')} icon={<NoTasksIcon />} padding='l' />
        )}

        <ConfirmationDialog />
      </>
    );
  },
);
